import React from 'react';
import { PasswordField, useToast } from '@chatsdet/ui';
import { ENV_VAR_MASK } from '@chatsdet/types';
import { toApiError } from '../../lib/api-error';

/**
 * A masked secret field that reveals its plaintext value on demand.
 *
 * `revealUrl` is the endpoint to fetch the plaintext from; pass `null` when no
 * reveal is possible (e.g. the owning entity hasn't been created yet) — in
 * that case visibility just toggles locally with no network call. Once the
 * current value no longer equals `ENV_VAR_MASK` (i.e. it's already plaintext,
 * likely just edited), visibility also toggles locally without a fetch.
 */
export function SensitiveRevealField({
  revealUrl,
  label,
  value,
  onChange,
  description,
  isDisabled,
}: {
  revealUrl: string | null;
  label: string;
  value: string;
  onChange: (value: string) => void;
  description?: string;
  isDisabled?: boolean;
}) {
  const { toast } = useToast();
  const [isVisible, setIsVisible] = React.useState(false);
  const [isRevealing, setIsRevealing] = React.useState(false);

  const handleVisibilityChange = async (visible: boolean) => {
    if (!visible) {
      setIsVisible(false);
      return;
    }

    if (!revealUrl || value !== ENV_VAR_MASK) {
      setIsVisible(true);
      return;
    }

    setIsRevealing(true);
    try {
      const response = await fetch(revealUrl, { credentials: 'include', cache: 'no-store' });
      if (!response.ok) throw await toApiError(response, `Failed to reveal ${label}`);
      const body = (await response.json()) as { value?: unknown };
      if (typeof body.value !== 'string') throw new Error(`Failed to reveal ${label}`);
      onChange(body.value);
      setIsVisible(true);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : `Could not reveal ${label}`);
    } finally {
      setIsRevealing(false);
    }
  };

  return (
    <PasswordField
      label={label}
      value={value}
      onChange={onChange}
      placeholder="(sensitive)"
      description={description}
      isVisible={isVisible}
      onVisibilityChange={handleVisibilityChange}
      isDisabled={isDisabled || isRevealing}
    />
  );
}
